import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { hqApi } from "@/hq/hqApi";

/**
 * TestModeContext — shared HQ-wide read of the backend Test Mode flag.
 * Provides `{ testMode, recipient, refresh }` so any HQ page can render a
 * consistent banner without each screen re-fetching independently.
 * Backend is authoritative — refresh() re-pulls after any change.
 */
const TestModeCtx = createContext({ testMode: false, recipient: null, refresh: () => {} });

export function TestModeProvider({ children }) {
  const [testMode, setTestMode] = useState(false);
  const [recipient, setRecipient] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const r = await hqApi.get("/settings/test-mode");
      setTestMode(!!r.data?.test_mode);
      setRecipient(r.data?.test_recipient_email || null);
    } catch { /* silent — the banner just stays hidden */ }
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  return (
    <TestModeCtx.Provider value={{ testMode, recipient, refresh }}>
      {children}
    </TestModeCtx.Provider>
  );
}

export function useTestMode() { return useContext(TestModeCtx); }

/**
 * TestModeBanner — persistent orange strip at the top of every HQ page
 * when Test Mode is ON. Hidden entirely when OFF.
 */
export function TestModeBanner() {
  const { testMode, recipient } = useTestMode();
  if (!testMode) return null;
  return (
    <div
      data-testid="hq-test-mode-banner"
      className="w-full bg-[#FF6A00] text-black px-4 lg:px-8 py-2 flex flex-wrap items-center gap-3 text-[11px] font-black tracking-[0.14em] uppercase border-b border-black/10"
    >
      <span aria-hidden>⚠</span>
      <span data-testid="hq-test-mode-banner-label">TEST MODE — NO LIVE PAYMENTS — EMAILS REDIRECTED</span>
      {recipient && (
        <span className="normal-case tracking-normal font-medium text-[11px] opacity-80"
              data-testid="hq-test-mode-banner-recipient">
          · Emails → <span className="font-black tracking-wide">{recipient}</span>
        </span>
      )}
    </div>
  );
}
